import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Skeleton from '@mui/material/Skeleton';
import TablePagination from '@mui/material/TablePagination';
import TextField from "@mui/material/TextField";
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import Users from '../../collections/users';
import { excuseApi } from '../../commons/http';

// Derived server-side; see the customer menu status story (story-03).
const menuStatusConfig = {
  HAS_MENU: { label: 'Có menu', color: 'success' },
  NEEDS_NEW_MENU: { label: 'Cần tạo menu mới', color: 'error' },
  MENU_ENDED: { label: 'Menu đã hết', color: 'warning' },
  NO_MENU: { label: 'Chưa có menu', color: 'default' },
};

const Customers = (props) => {
  useEffect(() => {
    props.setPageTitle('Danh sách khách hàng');
  });

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '0'));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('per_page') || '10'));
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [inputValue, setInputValue] = useState(searchParams.get('search') || "");
  const [tagsFilter, setTagsFilter] = useState(searchParams.get('tags') || "");
  const [tagsInputValue, setTagsInputValue] = useState(searchParams.get('tags') || "");
  const [availableTags, setAvailableTags] = useState([]);
  const [menuStatus, setMenuStatus] = useState(searchParams.get('menu_status') || "");
  const navigate = useNavigate();

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await excuseApi({
          path: '/tags',
          method: 'GET'
        });
        if (res.status === 200) {
          setAvailableTags(res.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // A single request for every combination of filters. The backend `name`
        // filter already searches name, phone_number and backup_phone_number, so
        // there is no reason for search to take a separate path - doing so used to
        // drop the tags filter whenever a search term was present.
        const users = new Users();
        const params = {};
        if (tagsFilter) {
          params.tags = tagsFilter;
        }
        if (menuStatus) {
          params.menu_status = menuStatus;
        }
        await users.fetch(page + 1, rowsPerPage, params, searchTerm);
        setFilteredUsers(users.items);
        setTotalUsers(users.total);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage, searchTerm, tagsFilter, menuStatus]);

  // Takes overrides rather than positional arguments: the filter list has grown
  // enough that positional defaults were silently dropping filters.
  const updateSearchParams = (overrides = {}) => {
    const next = {
      page,
      per_page: rowsPerPage,
      search: searchTerm,
      tags: tagsFilter,
      menu_status: menuStatus,
      ...overrides,
    };
    const params = new URLSearchParams();
    params.set('page', next.page.toString());
    params.set('per_page', next.per_page.toString());
    if (next.search) {
      params.set('search', next.search);
    }
    if (next.tags) {
      params.set('tags', next.tags);
    }
    if (next.menu_status) {
      params.set('menu_status', next.menu_status);
    }
    setSearchParams(params);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    updateSearchParams({ page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    updateSearchParams({ page: 0, per_page: newRowsPerPage });
  };

  const handleSearch = () => {
    setSearchTerm(inputValue);
    setPage(0);
    updateSearchParams({ page: 0, search: inputValue });
  };

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleTagsFilter = () => {
    setTagsFilter(tagsInputValue);
    setPage(0);
    updateSearchParams({ page: 0, tags: tagsInputValue });
  };

  const handleMenuStatusChange = (e) => {
    const newMenuStatus = e.target.value;
    setMenuStatus(newMenuStatus);
    setPage(0);
    updateSearchParams({ page: 0, menu_status: newMenuStatus });
  };

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        variant="rectangular"
        height={250}
        sx={{ m: 3 }}
      />
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/users/new')}
            sx={{ textTransform: 'none', mb: 1 }}
            startIcon={<AddIcon />}
          >
            Thêm khách hàng
          </Button>
        </Grid>
        <Grid item>
          <Autocomplete
            freeSolo
            options={availableTags}
            inputValue={tagsInputValue}
            onInputChange={(event, newValue) => {
              setTagsInputValue(newValue);
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                setTagsInputValue(newValue);
                setTagsFilter(newValue);
                setPage(0);
                updateSearchParams({ page: 0, tags: newValue });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleTagsFilter();
              }
            }}
            onBlur={handleTagsFilter}
            sx={{ width: "200px" }}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Lọc theo tags..."
                size="small"
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  <Chip
                    label={option}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </li>
              );
            }}
          />
        </Grid>
        <Grid item>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="menu-status-filter-label">Trạng thái menu</InputLabel>
            <Select
              labelId="menu-status-filter-label"
              id="menu-status-filter"
              value={menuStatus}
              label="Trạng thái menu"
              onChange={handleMenuStatusChange}
            >
              <MenuItem value="">Tất cả</MenuItem>
              {Object.entries(menuStatusConfig).map(([value, config]) => (
                <MenuItem key={value} value={value}>{config.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item sx={{ marginLeft: 'auto' }}>
          <TextField
            sx={{
              width: "300px",
            }}
            value={inputValue}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            onBlur={handleSearch}
            id="search-customers"
            name="search"
            type="text"
            placeholder="Tìm khách hàng..."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell align="left">Tên khách hàng</TableCell>
              <TableCell align="left">Số điện thoại</TableCell>
              <TableCell align="left">Địa chỉ</TableCell>
              <TableCell align="left">Tags</TableCell>
              <TableCell align="left">Menu</TableCell>
              <TableCell align="left">Nguồn</TableCell>
              <TableCell align="left">Ghi chú</TableCell>
              <TableCell align="left">Thời gian tạo</TableCell>
              <TableCell align="left">Thay đổi cuối</TableCell>
              <TableCell align="left">Chỉnh sửa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map(user => (
              <TableRow
                key={user.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
                onClick={() => navigate(`/customers/${user.id}`)}

              >
                <TableCell component="th" scope="row">{user.id}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} align="left">{user.name}</TableCell>
                <TableCell align="left">{user.phone_number}</TableCell>
                <TableCell align="left">{user.address}</TableCell>
                <TableCell align="left">
                  {user.tags ? (
                    user.tags.split(',').map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag.trim()}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))
                  ) : null}
                </TableCell>
                <TableCell align="left">
                  {user.menu_status ? (
                    <Box>
                      <Tooltip
                        title={
                          user.active_menu_title
                            || (user.menu_last_delivery_date
                              ? `Lần giao cuối: ${new Date(user.menu_last_delivery_date).toLocaleDateString('vi-VN')}`
                              : '')
                        }
                      >
                        <Chip
                          label={menuStatusConfig[user.menu_status]?.label || user.menu_status}
                          color={menuStatusConfig[user.menu_status]?.color || 'default'}
                          size="small"
                          variant="outlined"
                          onClick={user.active_menu_id
                            ? (e) => {
                              // keep the row's navigate-to-customer out of it
                              e.stopPropagation();
                              navigate(`/menus/${user.active_menu_id}/edit`);
                            }
                            : undefined}
                          sx={{ cursor: user.active_menu_id ? 'pointer' : 'default' }}
                        />
                      </Tooltip>
                      {user.menu_status !== 'HAS_MENU' && user.menu_last_delivery_date && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {new Date(user.menu_last_delivery_date).toLocaleDateString('vi-VN')}
                        </Typography>
                      )}
                    </Box>
                  ) : null}
                </TableCell>
                <TableCell align="left">{user.referral_source}</TableCell>
                <TableCell align="left">{user.note}</TableCell>
                <TableCell align="left">
                  {new Date(user.created_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                </TableCell>
                <TableCell align="left">
                  {new Date(user.updated_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none', mb: 1, textAlign: 'center' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customers/${user.id}`)
                    }}
                  >
                    Chỉnh sửa
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalUsers}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Số hàng mỗi trang"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
      />
    </Container>
  );
};

export default Customers;
